import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Paper,
  Tooltip,
  Stack,
  alpha,
  Popover,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Document } from "../types";
import theme from "../../../styles/styleTheme";
import { Modal } from "../../common/Modal";
import ImageRedactor from "../../../submodules/image-redactor/ImageRedactor";

interface DocumentUploadSectionProps {
  documents: Document[];
  onFileChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string,
    redactedImage?: string
  ) => void;
  onViewDocument: (document: Document) => void;
  employmentType: string;
}

const DOCUMENT_REQUIREMENTS = {
  EMPLOYEE: {
    IDENTITY_FRONT: {
      required: true,
      label: "screening_profile.doc_identity_front",
      info: "screening_profile.identity_front_info",
    },
    IDENTITY_BACK: {
      required: true,
      label: "screening_profile.doc_identity_back",
      info: "screening_profile.identity_back_info",
    },
    EMPLOYER_DECLARATION: {
      required: true,
      label: "screening_profile.doc_employer_declaration",
      info: "screening_profile.employer_declaration_info",
    },
    PAYSLIP_1: {
      required: true,
      label: "screening_profile.doc_payslip_1",
      info: "screening_profile.payslip_1_info",
    },
    PAYSLIP_2: {
      required: true,
      label: "screening_profile.doc_payslip_2",
      info: "screening_profile.payslip_2_info",
    },
    PAYSLIP_3: {
      required: true,
      label: "screening_profile.doc_payslip_3",
      info: "screening_profile.payslip_3_info",
    },
    BANKCARD_COPY: {
      required: true,
      label: "screening_profile.doc_bankcard_copy",
      info: "screening_profile.bank_card_info",
    },
    LANDLORD_DECLARATION: {
      required: false,
      label: "screening_profile.doc_landlord_declaration",
      info: "screening_profile.landlord_declaration_info",
    },
    PERSONAL_RECORD: {
      required: false,
      label: "screening_profile.doc_personal_record",
      info: "screening_profile.personal_record_info",
    },
    UWV_RECORD: {
      required: false,
      label: "screening_profile.doc_uwv_record",
      info: "screening_profile.uwv_record_info",
    },
  },
  SELF_EMPLOYED: {
    IDENTITY_FRONT: {
      required: true,
      label: "screening_profile.doc_identity_front",
      info: "screening_profile.identity_front_info",
    },
    IDENTITY_BACK: {
      required: true,
      label: "screening_profile.doc_identity_back",
      info: "screening_profile.identity_back_info",
    },
    COMPANY_REGISTRATION: {
      required: true,
      label: "screening_profile.doc_company_registration",
      info: "screening_profile.company_registration_info",
    },
    INCOME_STATEMENT: {
      required: true,
      label: "screening_profile.doc_income_statement",
      info: "screening_profile.income_statement_info",
    },
    BALANCE_STATEMENT: {
      required: true,
      label: "screening_profile.doc_balance_statement",
      info: "screening_profile.balance_statement_info",
    },
    BANKCARD_COPY: {
      required: true,
      label: "screening_profile.doc_bankcard_copy",
      info: "screening_profile.bank_card_info",
    },
    LANDLORD_DECLARATION: {
      required: false,
      label: "screening_profile.doc_landlord_declaration",
      info: "screening_profile.landlord_declaration_info",
    },
    PERSONAL_RECORD: {
      required: false,
      label: "screening_profile.doc_personal_record",
      info: "screening_profile.personal_record_info",
    },
  },
  PENSION: {
    IDENTITY_FRONT: {
      required: true,
      label: "screening_profile.doc_identity_front",
      info: "screening_profile.identity_front_info",
    },
    IDENTITY_BACK: {
      required: true,
      label: "screening_profile.doc_identity_back",
      info: "screening_profile.identity_back_info",
    },
    AOW: {
      required: true,
      label: "screening_profile.doc_aow_statement",
      info: "screening_profile.aow_statement_info",
    },
    PENSION: {
      required: true,
      label: "screening_profile.doc_pension_statement",
      info: "screening_profile.pension_statement_info",
    },
    BANKCARD_COPY: {
      required: true,
      label: "screening_profile.doc_bankcard_copy",
      info: "screening_profile.bank_card_info",
    },
    LANDLORD_DECLARATION: {
      required: false,
      label: "screening_profile.doc_landlord_declaration",
      info: "screening_profile.landlord_declaration_info",
    },
    PERSONAL_RECORD: {
      required: false,
      label: "screening_profile.doc_personal_record",
      info: "screening_profile.personal_record_info",
    },
  },
};

const DocumentUploadSection: React.FC<DocumentUploadSectionProps> = ({
  documents,
  onFileChange,
  onViewDocument,
  employmentType,
}) => {
  const { t } = useTranslation();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [currentInfo, setCurrentInfo] = useState<string>("");
  const [redactionModalOpen, setRedactionModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>("");
  const [previewUrl, setPreviewUrl] = useState<string>("");

  const documentConfig =
    DOCUMENT_REQUIREMENTS[
      employmentType as keyof typeof DOCUMENT_REQUIREMENTS
    ] || DOCUMENT_REQUIREMENTS.EMPLOYEE;

  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const isValidFileType = (file: File, documentType: string) => {
    if (documentType === "IDENTITY_FRONT" || documentType === "IDENTITY_BACK") {
      return file.type.startsWith("image/");
    }
    return file.type.startsWith("image/") || file.type === "application/pdf";
  };

  const handleFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>,
    documentType: string
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      if (!isValidFileType(file, documentType)) {
        alert(t(documentType === "IDENTITY_FRONT" || documentType === "IDENTITY_BACK" 
          ? "screening_profile.only_images_allowed" 
          : "screening_profile.only_images_and_pdf_allowed"));
        return;
      }

      if (
        documentType === "IDENTITY_FRONT" ||
        documentType === "IDENTITY_BACK"
      ) {
        setSelectedFile(file);
        setSelectedDocumentType(documentType);
        setPreviewUrl(URL.createObjectURL(file));
        setRedactionModalOpen(true);
      } else {
        onFileChange(event, documentType);
      }
    }
  };

  const handleRedactedImageSave = (dataUrl: string) => {
    if (selectedFile) {
      fetch(dataUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const redactedFile = new File([blob], selectedFile.name, {
            type: selectedFile.type,
          });

          const syntheticEvent = {
            target: {
              files: [redactedFile],
            },
          } as unknown as React.ChangeEvent<HTMLInputElement>;

          onFileChange(syntheticEvent, selectedDocumentType);
        });
    }
    setRedactionModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const open = Boolean(anchorEl);

  return (
    <Box>
      <Grid container spacing={2}>
        {Object.entries(documentConfig).map(([documentType, config]) => {
          const hasDocument = documents.find(
            (doc) => doc.screeningDocumentType === documentType
          );

          return (
            <Grid item xs={12} sm={6} md={4} key={documentType}>
              <Paper
                elevation={0}
                sx={{
                  p: 1,
                  height: "80px",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid",
                  borderColor: hasDocument
                    ? alpha("#4caf50", 0.3)
                    : config.required
                    ? alpha("#ff9800", 0.3)
                    : alpha("#1976d2", 0.1),
                  borderRadius: 2,
                  background: hasDocument
                    ? alpha("#4caf50", 0.05)
                    : config.required
                    ? alpha("#ffffff", 0.05)
                    : alpha("#ffffff", 0.8),
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "2px",
                    background: hasDocument
                      ? "linear-gradient(90deg, #4caf50, transparent)"
                      : config.required
                      ? "linear-gradient(90deg, #ff9800, transparent)"
                      : "linear-gradient(90deg, #EEEEEE, transparent)",
                    opacity: 0.5,
                  },
                }}
              >
                <Stack spacing={0.5}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Tooltip
                        title={
                          hasDocument
                            ? t("screening_profile.document_uploaded")
                            : t("screening_profile.doc_document")
                        }
                      >
                        <IconButton
                          component="label"
                          size="small"
                          sx={{
                            color: hasDocument
                              ? "success.main"
                              : config.required
                              ? theme.palette.secondary.main
                              : "#aaa",
                            border: "1px solid",
                            borderColor: hasDocument
                              ? alpha("#4caf50", 0.3)
                              : config.required
                              ? alpha("#ff9800", 0.3)
                              : alpha("#1976d2", 0.3),
                            background: hasDocument
                              ? alpha("#4caf50", 0.03)
                              : config.required
                              ? alpha("#ff9800", 0.03)
                              : alpha("#1976d2", 0.03),
                            "&:hover": {
                              transform: "scale(1.05)",
                              background: hasDocument
                                ? alpha("#4caf50", 0.08)
                                : config.required
                                ? alpha("#ff9800", 0.08)
                                : alpha("#1976d2", 0.08),
                              borderColor: hasDocument
                                ? alpha("#4caf50", 0.5)
                                : config.required
                                ? alpha("#ff9800", 0.5)
                                : alpha("#1976d2", 0.5),
                            },
                            width: "24px",
                            height: "24px",
                            transition: "all 0.2s ease-in-out",
                          }}
                        >
                          {hasDocument ? (
                            <CheckCircleIcon sx={{ fontSize: "0.875rem" }} />
                          ) : (
                            <CloudUploadIcon sx={{ fontSize: "0.875rem" }} />
                          )}
                          <input
                            type="file"
                            hidden
                            onClick={(e: any) => (e.target.value = null)}
                            onChange={(e) => handleFileSelect(e, documentType)}
                          />
                        </IconButton>
                      </Tooltip>

                      {config.required && (
                        <Tooltip title={t("screening_profile.required")}>
                          <ErrorOutlineIcon
                            sx={{
                              fontSize: "1.2rem",
                              color: "#cd0000",
                              mr: 0.5,
                            }}
                          />
                        </Tooltip>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 500,
                          letterSpacing: "0.3px",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t(config.label)}
                      </Typography>
                    </Box>
                    {hasDocument && (
                      <Tooltip title={t("screening_profile.view_document")}>
                        <IconButton
                          onClick={() =>
                            onViewDocument(
                              documents.find(
                                (doc) =>
                                  doc.screeningDocumentType === documentType
                              )!
                            )
                          }
                          size="small"
                          sx={{
                            color: "secondary.main",
                            border: "1px solid",
                            borderColor: "secondary.main",
                            "&:hover": {
                              color: "primary.dark",
                              borderColor: "primary.dark",
                              transform: "scale(1.05)",
                              background: alpha("#1976d2", 0.1),
                            },
                            transition: "all 0.2s ease-in-out",
                            width: "24px",
                            height: "24px",
                          }}
                        >
                          <VisibilityIcon sx={{ fontSize: "0.75rem" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      pl: 0.5,
                    }}
                  >
                    {t(config.info)}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      <Modal
        title={t("screening_profile.redact_document")}
        open={redactionModalOpen}
        setOpen={setRedactionModalOpen}
        size="md"
        actions={false}
        headerCloseButton={true}
        onClose={() => setRedactionModalOpen(false)}
        fullScreen={isMobile}
      >
        {previewUrl && (
          <ImageRedactor
            src={previewUrl}
            onSave={handleRedactedImageSave}
            documentType={selectedDocumentType}
          />
        )}
      </Modal>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPopover-paper": {
            maxWidth: "300px",
            p: 2,
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
          },
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", fontSize: "0.875rem" }}
        >
          {t(currentInfo)}
        </Typography>
      </Popover>
    </Box>
  );
};

export default DocumentUploadSection;
